import React, { useContext, useState, useRef } from "react";
import "../AddChatRoomModal/AddChatRoomModal.css";
import { IoCloseOutline, IoDocumentTextOutline, IoCloudUploadOutline, IoTrashOutline } from "react-icons/io5";
import { TbStack2 } from "react-icons/tb";
import { useLanguage } from "../../../../LanguageProvider";
import { useToaster } from "../../../../ToasterProvider";
import AddModalInputField from "../../../../components/AddModalInputField/AddModalInputField";
import { GeneralContext } from "../../../../GeneralProvider";
import Services from "../../../../services/Services";
import "./AddGalleryFileModal.css";

export default function AddGalleryFileModal({
    isOpen,
    onClose,
    selectedChannel,
    getCollectionDetail,
    collectionID
}) {
    const { setCreateProjectLoading } = useContext(GeneralContext);
    const { getTranslation } = useLanguage();
    const { toastWarning, toastSuccess } = useToaster();
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const resetStates = () => {
        setSelectedFiles([])
        setTitle("")
        setDescription("")
        setIsDragOver(false)
    }

    const toBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const handleFiles = async (files) => {
        const fileArray = Array.from(files);
        const newFiles = [];

        for (const file of fileArray) {
            const base64String = await toBase64(file);
            const extension = file.name.split(".").pop();
            const titleOnly = file.name.substring(0, file.name.lastIndexOf(".")) || file.name;
            // Generate a more unique ID with timestamp and random number - aynı isimli dosyaların aynı id'ye sahip olmasını önlemek için
            const fileId = `${file.name}_${file.size}_${file.lastModified}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            newFiles.push({
                id: fileId,
                name: file.name,
                size: file.size,
                type: file.type,
                file: base64String,
                extension,
                title: titleOnly
            });
        }

        setSelectedFiles(prev => [...prev, ...newFiles]);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFiles(files);
        }
    };

    const handleFileSelect = (e) => {
        const files = e.target.files;
        if (files.length > 0) {
            handleFiles(files);
        }
    };

    const removeFile = (fileId) => {
        setSelectedFiles(prev => prev.filter(file => file.id !== fileId));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const createFile = () => {
        setCreateProjectLoading(true)
        const token = localStorage.getItem("token")
        let fileTitle = title ? title : false;
        
        // Convert selectedFiles to the format expected by the service
        const filesForService = selectedFiles.map(file => ({
            _key: file.id,
            file: file.file,
            extension: file.extension,
            title: file.title
        }));
        
        Services.SocialHiveAddFilesToCollection(token, selectedChannel.channelID, collectionID, fileTitle, description, filesForService)
            .then((res) => {
                setCreateProjectLoading(false)
                if (res.data.status) {
                    toastSuccess(getTranslation("successMessage"));
                    onClose();
                    resetStates();
                    getCollectionDetail();
                } else {
                    toastWarning(res.data.message || getTranslation("somethingWentWrong"));
                }
            })
            .catch((err) => {
                console.error(err);
                toastWarning(getTranslation("somethingWentWrong"));
            });
    }

    const onComplete = () => {
        createFile()
        resetStates()
        onClose()
    };

    const handleOnClose = () => {
        resetStates()
        onClose()
    }

    if (!isOpen) return null;

    return (
        <div className="add-room-modal-overlay">
            <div className="add-room-modal-container">
                <IoCloseOutline
                    className="add-room-modal-button close"
                    onClick={handleOnClose}
                />

                <p className="add-room-modal-title">{getTranslation("addNewFile")}</p>

                {/* Custom Drag & Drop Section */}
                <div 
                    className={`gallery-drag-drop-area ${isDragOver ? 'drag-over' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <IoCloudUploadOutline size={48} className="gallery-upload-icon" />
                    <p className="gallery-drag-drop-text">
                        {getTranslation("clickToUploadOrDrag")}
                    </p>
                    <p className="gallery-drag-drop-subtext">
                        {getTranslation("uploadSubtext")}
                    </p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />
                </div>

                {/* File List */}
                {selectedFiles.length > 0 && (
                    <div className="gallery-file-list">
                        <h4 className="gallery-file-list-title">{selectedFiles.length} {getTranslation("remaining")}</h4>
                        {selectedFiles.map((file) => (
                            <div key={file.id} className="gallery-file-item">
                                <div className="gallery-file-info">
                                    <div className="gallery-file-name">{file.name}</div>
                                    <div className="gallery-file-details">
                                        {formatFileSize(file.size)} • {file.extension.toUpperCase()}
                                    </div>
                                </div>
                                <button 
                                    className="gallery-file-remove"
                                    onClick={() => removeFile(file.id)}
                                    title={getTranslation("delete")}
                                >
                                    <IoTrashOutline size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Show title/description only when exactly 1 file selected */}
                {selectedFiles.length === 1 && (
                    <>
                        <AddModalInputField
                            icon={<TbStack2 size={22} style={{ width: "25px" }} />}
                            placeholder={getTranslation("title")}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            type={"text"}
                        />

                        <div className="add-task-modal-content">
                            <IoDocumentTextOutline
                                size={20}
                                style={{
                                    width: "25px",
                                    alignSelf: "flex-start",
                                    marginTop: "10px",
                                }}
                            />
                            <textarea
                                className="add-task-modal-textarea"
                                placeholder={getTranslation("description")}
                                value={description}
                                rows={6}
                                onChange={(e) => setDescription(e.target.value)}
                            ></textarea>
                        </div>
                    </>
                )}

                {selectedFiles.length > 0 && (
                    <div className="add-room-modal-actions">
                        <button onClick={onComplete} className="add-room-modal-button create">
                            {getTranslation("uploadFile")}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
